import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { RadiologyTestNameOption } from '../../services/radiologyTestService';
import { formatModalityDisplayLabel } from '../../utils/modalityUtils';
import { parseStoredTestNames } from '../../utils/testNameListUtils';
import { ICON_STROKE_WIDTH } from '../../constants';

interface RadiologyTestNamePickerProps {
  id: string;
  selectedTestNames: string[];
  onChange: (testNames: string[]) => void;
  tests: RadiologyTestNameOption[];
  loading?: boolean;
  optional?: boolean;
  optionalLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  multiple?: boolean;
}

const CHIP_LABEL_MAX_LENGTH = 48;

function formatTestLabel(test: RadiologyTestNameOption): string {
  const modality = test.modality ? formatModalityDisplayLabel(test.modality) : '';
  return modality ? `${test.test_name} (${modality})` : test.test_name;
}

function truncateLabel(label: string, maxLength = CHIP_LABEL_MAX_LENGTH): string {
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, maxLength - 1)}…`;
}

function flattenSelectedTestNames(names: string[]): string[] {
  const flattened: string[] = [];
  const seen = new Set<string>();

  for (const raw of names) {
    const entries = parseStoredTestNames(raw);
    const values = entries.length ? entries : [String(raw || '').trim()].filter(Boolean);
    for (const value of values) {
      const key = value.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      flattened.push(value);
    }
  }

  return flattened;
}

function resolveChipLabel(testName: string, tests: RadiologyTestNameOption[]): string {
  const match = tests.find(test => test.test_name.toLowerCase() === testName.toLowerCase());
  return truncateLabel(match ? formatTestLabel(match) : testName);
}

function TestOptionButton({
  test,
  selected,
  onToggle,
}: Readonly<{
  test: RadiologyTestNameOption;
  selected: boolean;
  onToggle: (testName: string) => void;
}>) {
  const label = formatTestLabel(test);
  return (
    <button
      key={`${test.test_name}-${test.modality ?? ''}`}
      type="button"
      className={['add-routing-rule-study-option', selected ? 'selected' : '']
        .filter(Boolean)
        .join(' ')}
      onClick={() => onToggle(test.test_name)}
      title={selected ? `Deselect ${label}` : `Select ${label}`}
    >
      <span className="add-routing-rule-study-option-name">{test.test_name}</span>
      {test.modality ? (
        <span className="add-routing-rule-study-option-modality">
          {formatModalityDisplayLabel(test.modality)}
        </span>
      ) : null}
      {selected ? (
        <span
          className="radiology-test-name-picker-option-check"
          aria-hidden="true"
        >
          ✓
        </span>
      ) : null}
    </button>
  );
}

function MultiSelectTrigger({
  id,
  open,
  loading,
  disabled,
  placeholder,
  normalizedSelected,
  tests,
  onToggleDropdown,
  onRemove,
}: Readonly<{
  id: string;
  open: boolean;
  loading: boolean;
  disabled: boolean;
  placeholder: string;
  normalizedSelected: string[];
  tests: RadiologyTestNameOption[];
  onToggleDropdown: () => void;
  onRemove: (testName: string) => void;
}>) {
  let inner: React.ReactNode;
  if (loading) {
    inner = <span className="add-routing-rule-placeholder">Loading tests…</span>;
  } else if (normalizedSelected.length === 0) {
    inner = <span className="add-routing-rule-placeholder">{placeholder}</span>;
  } else {
    inner = normalizedSelected.map(testName => (
      <span
        key={testName}
        className="radiology-test-name-picker-chip"
      >
        <span
          className="radiology-test-name-picker-chip-label"
          title={testName}
        >
          {resolveChipLabel(testName, tests)}
        </span>
        <button
          type="button"
          className="radiology-test-name-picker-chip-remove"
          onClick={event => {
            event.stopPropagation();
            onRemove(testName);
          }}
          disabled={disabled || loading}
          aria-label={`Remove ${testName}`}
        >
          <X
            size={14}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
      </span>
    ));
  }

  return (
    <div
      id={id}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      tabIndex={disabled || loading ? -1 : 0}
      className={[
        'add-routing-rule-study-trigger',
        'radiology-test-name-picker-box',
        open ? 'open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onToggleDropdown}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggleDropdown();
        }
      }}
    >
      <div
        className="radiology-test-name-picker-box-inner"
        aria-label="Selected tests"
      >
        {inner}
      </div>
    </div>
  );
}

function SingleSelectTrigger({
  id,
  open,
  loading,
  disabled,
  placeholder,
  normalizedSelected,
  tests,
  onToggleDropdown,
}: Readonly<{
  id: string;
  open: boolean;
  loading: boolean;
  disabled: boolean;
  placeholder: string;
  normalizedSelected: string[];
  tests: RadiologyTestNameOption[];
  onToggleDropdown: () => void;
}>) {
  return (
    <button
      id={id}
      type="button"
      className={['add-routing-rule-study-trigger', open ? 'open' : ''].filter(Boolean).join(' ')}
      disabled={disabled || loading}
      onClick={onToggleDropdown}
      aria-expanded={open}
      aria-haspopup="listbox"
    >
      {normalizedSelected.length === 0 ? (
        <span className="add-routing-rule-placeholder">
          {loading ? 'Loading tests…' : placeholder}
        </span>
      ) : (
        <span
          className="add-routing-rule-study-summary"
          title={normalizedSelected[0]}
        >
          {resolveChipLabel(normalizedSelected[0], tests)}
        </span>
      )}
    </button>
  );
}

function PickerOptionsList({
  loading,
  optional,
  optionalLabel,
  multiple,
  search,
  selectedTestsInList,
  unselectedFilteredTests,
  filteredTests,
  emptyMessage,
  normalizedSelected,
  isSelected,
  onToggle,
  onClear,
}: Readonly<{
  loading: boolean;
  optional: boolean;
  optionalLabel: string;
  multiple: boolean;
  search: string;
  selectedTestsInList: RadiologyTestNameOption[];
  unselectedFilteredTests: RadiologyTestNameOption[];
  filteredTests: RadiologyTestNameOption[];
  emptyMessage: string;
  normalizedSelected: string[];
  isSelected: (testName: string) => boolean;
  onToggle: (testName: string) => void;
  onClear: () => void;
}>) {
  if (loading) {
    return <div className="add-routing-rule-study-option muted">Loading tests…</div>;
  }

  const showSelectedSection = multiple && selectedTestsInList.length > 0 && !search.trim();
  const testsToRender = multiple && !search.trim() ? unselectedFilteredTests : filteredTests;
  const showEmpty = filteredTests.length === 0 && selectedTestsInList.length === 0;

  return (
    <>
      {optional ? (
        <button
          type="button"
          className={[
            'add-routing-rule-study-option',
            normalizedSelected.length === 0 ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onClear}
        >
          <span className="add-routing-rule-study-option-name">{optionalLabel}</span>
        </button>
      ) : null}
      {showSelectedSection ? (
        <>
          <div className="radiology-test-name-picker-section-label">Selected</div>
          {selectedTestsInList.map(test => (
            <TestOptionButton
              key={`selected-${test.test_name}-${test.modality ?? ''}`}
              test={test}
              selected
              onToggle={onToggle}
            />
          ))}
          {unselectedFilteredTests.length > 0 ? (
            <div className="radiology-test-name-picker-section-label">All tests</div>
          ) : null}
        </>
      ) : null}
      {showEmpty ? (
        <div className="add-routing-rule-study-option muted">{emptyMessage}</div>
      ) : (
        testsToRender.map(test => (
          <TestOptionButton
            key={`${test.test_name}-${test.modality ?? ''}`}
            test={test}
            selected={isSelected(test.test_name)}
            onToggle={onToggle}
          />
        ))
      )}
    </>
  );
}

function PickerDropdown({
  search,
  onSearchChange,
  loading,
  optional,
  optionalLabel,
  multiple,
  selectedTestsInList,
  unselectedFilteredTests,
  filteredTests,
  emptyMessage,
  normalizedSelected,
  isSelected,
  onToggle,
  onClear,
  onDone,
}: Readonly<{
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  optional: boolean;
  optionalLabel: string;
  multiple: boolean;
  selectedTestsInList: RadiologyTestNameOption[];
  unselectedFilteredTests: RadiologyTestNameOption[];
  filteredTests: RadiologyTestNameOption[];
  emptyMessage: string;
  normalizedSelected: string[];
  isSelected: (testName: string) => boolean;
  onToggle: (testName: string) => void;
  onClear: () => void;
  onDone: () => void;
}>) {
  return (
    <div className="add-routing-rule-study-dropdown radiology-test-name-picker-dropdown">
      <input
        type="text"
        className="add-routing-rule-study-search"
        placeholder="Search test name…"
        value={search}
        onChange={event => onSearchChange(event.target.value)}
        onMouseDown={event => event.stopPropagation()}
        autoFocus
      />
      <div className="add-routing-rule-study-options">
        <PickerOptionsList
          loading={loading}
          optional={optional}
          optionalLabel={optionalLabel}
          multiple={multiple}
          search={search}
          selectedTestsInList={selectedTestsInList}
          unselectedFilteredTests={unselectedFilteredTests}
          filteredTests={filteredTests}
          emptyMessage={emptyMessage}
          normalizedSelected={normalizedSelected}
          isSelected={isSelected}
          onToggle={onToggle}
          onClear={onClear}
        />
      </div>
      {multiple ? (
        <div className="radiology-test-name-picker-footer">
          <span className="radiology-test-name-picker-count">
            {normalizedSelected.length
              ? `${normalizedSelected.length} selected`
              : 'Select one or more tests'}
          </span>
          <div className="radiology-test-name-picker-footer-actions">
            {normalizedSelected.length > 0 ? (
              <button
                type="button"
                className="radiology-test-name-picker-clear"
                onClick={onClear}
              >
                Clear
              </button>
            ) : null}
            <button
              type="button"
              className="radiology-test-name-picker-done"
              onClick={onDone}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildSelectedTestsInList(
  multiple: boolean,
  normalizedSelected: string[],
  tests: RadiologyTestNameOption[],
  isSelected: (testName: string) => boolean
): RadiologyTestNameOption[] {
  if (!multiple || normalizedSelected.length === 0) {
    return [];
  }
  const fromCatalog = tests.filter(test => isSelected(test.test_name));
  const catalogKeys = new Set(fromCatalog.map(test => test.test_name.toLowerCase()));
  const customOnly = normalizedSelected
    .filter(name => !catalogKeys.has(name.toLowerCase()))
    .map(name => ({
      test_name: name,
      modality: null,
      id: null,
      test_code: null,
      is_active: null,
    }));
  return [...fromCatalog, ...customOnly];
}

export default function RadiologyTestNamePicker({
  id,
  selectedTestNames,
  onChange,
  tests,
  loading = false,
  optional = false,
  optionalLabel = 'Any test',
  disabled = false,
  placeholder = 'Select test names…',
  emptyMessage = 'No tests found',
  multiple = true,
}: Readonly<RadiologyTestNamePickerProps>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedSelected = useMemo(
    () => flattenSelectedTestNames(selectedTestNames),
    [selectedTestNames]
  );

  const selectedKeySet = useMemo(
    () => new Set(normalizedSelected.map(name => name.toLowerCase())),
    [normalizedSelected]
  );

  const isSelected = useCallback(
    (testName: string) => selectedKeySet.has(testName.trim().toLowerCase()),
    [selectedKeySet]
  );

  const filteredTests = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return tests;
    }
    return tests.filter(test => {
      const label = formatTestLabel(test).toLowerCase();
      return label.includes(query) || test.test_name.toLowerCase().includes(query);
    });
  }, [tests, search]);

  const selectedTestsInList = useMemo(
    () => buildSelectedTestsInList(multiple, normalizedSelected, tests, isSelected),
    [multiple, normalizedSelected, tests, isSelected]
  );

  const unselectedFilteredTests = useMemo(() => {
    if (!multiple) {
      return filteredTests;
    }
    return filteredTests.filter(test => !isSelected(test.test_name));
  }, [filteredTests, multiple, isSelected]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const updateSelection = (nextNames: string[]) => {
    onChange(flattenSelectedTestNames(nextNames));
  };

  const handleRemove = (testName: string) => {
    const key = testName.trim().toLowerCase();
    updateSelection(normalizedSelected.filter(name => name.toLowerCase() !== key));
  };

  const handleToggle = (testName: string) => {
    if (multiple) {
      if (isSelected(testName)) {
        handleRemove(testName);
      } else {
        updateSelection([...normalizedSelected, testName]);
      }
      return;
    }

    updateSelection([testName]);
    setOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    updateSelection([]);
    if (!multiple) {
      setOpen(false);
    }
    setSearch('');
  };

  const toggleDropdown = () => {
    if (disabled || loading) {
      return;
    }
    setOpen(current => !current);
  };

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      className="radiology-test-name-picker"
      ref={containerRef}
    >
      <div className="add-routing-rule-study-types">
        {multiple ? (
          <MultiSelectTrigger
            id={id}
            open={open}
            loading={loading}
            disabled={disabled}
            placeholder={placeholder}
            normalizedSelected={normalizedSelected}
            tests={tests}
            onToggleDropdown={toggleDropdown}
            onRemove={handleRemove}
          />
        ) : (
          <SingleSelectTrigger
            id={id}
            open={open}
            loading={loading}
            disabled={disabled}
            placeholder={placeholder}
            normalizedSelected={normalizedSelected}
            tests={tests}
            onToggleDropdown={toggleDropdown}
          />
        )}

        {open ? (
          <PickerDropdown
            search={search}
            onSearchChange={setSearch}
            loading={loading}
            optional={optional}
            optionalLabel={optionalLabel}
            multiple={multiple}
            selectedTestsInList={selectedTestsInList}
            unselectedFilteredTests={unselectedFilteredTests}
            filteredTests={filteredTests}
            emptyMessage={emptyMessage}
            normalizedSelected={normalizedSelected}
            isSelected={isSelected}
            onToggle={handleToggle}
            onClear={handleClear}
            onDone={closeDropdown}
          />
        ) : null}
      </div>
    </div>
  );
}
